import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  MapPin, Phone, Clock, Shield, Star, CheckCircle, 
  Wrench, Wind, Award, Users, ArrowRight,
  Building, Zap, Calendar, MessageCircle, ChevronDown,
  Snowflake, ThermometerSun, Sun
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// Türkiye genelinde hizmet verilen şehirler
const sehirler = [
  { slug: 'istanbul', name: 'İstanbul', population: '16M+' },
  { slug: 'ankara', name: 'Ankara', population: '5.7M+' },
  { slug: 'izmir', name: 'İzmir', population: '4.4M+' },
  { slug: 'bursa', name: 'Bursa', population: '3.1M+' },
  { slug: 'antalya', name: 'Antalya', population: '2.6M+' },
  { slug: 'adana', name: 'Adana', population: '2.2M+' },
  { slug: 'konya', name: 'Konya', population: '2.3M+' },
  { slug: 'gaziantep', name: 'Gaziantep', population: '2.1M+' },
  { slug: 'mersin', name: 'Mersin', population: '1.9M+' },
  { slug: 'kayseri', name: 'Kayseri', population: '1.4M+' },
  { slug: 'eskisehir', name: 'Eskişehir', population: '900K+' },
  { slug: 'denizli', name: 'Denizli', population: '1M+' },
];

// Klima markaları
const klimaMarkalari = [
  { name: 'Daikin', color: '#00A0E4' },
  { name: 'Mitsubishi', color: '#E60012' },
  { name: 'Samsung', color: '#1428A0' },
  { name: 'LG', color: '#A50034' },
  { name: 'Arçelik', color: '#1E40AF' },
  { name: 'Vestel', color: '#0D47A1' },
  { name: 'Toshiba', color: '#FF0000' },
  { name: 'Panasonic', color: '#0068B7' },
  { name: 'General', color: '#E31937' },
  { name: 'Carrier', color: '#005DAA' },
  { name: 'Airfel', color: '#2563EB' },
  { name: 'Beko', color: '#DC2626' },
];

// Klima arıza kodları
const klimaArizaKodlari = {
  daikin: [
    { kod: 'E1', anlam: 'İç ünite PCB hatası', cozum: 'Ana kartı kontrol ettirin.' },
    { kod: 'E3', anlam: 'Yüksek basınç hatası', cozum: 'Gaz kaçağı veya tıkanıklık olabilir.' },
    { kod: 'E4', anlam: 'Düşük basınç hatası', cozum: 'Gaz dolumu gerekebilir.' },
    { kod: 'E5', anlam: 'Kompresör aşırı yük', cozum: 'Servis çağırın.' },
    { kod: 'E6', anlam: 'İç fan motoru arızası', cozum: 'Fan motoru kontrolü gerekli.' },
    { kod: 'E7', anlam: 'Dış fan motoru arızası', cozum: 'Dış ünite servisi gerekli.' },
    { kod: 'F3', anlam: 'Boru sıcaklık sensörü', cozum: 'Sensör değişimi.' },
  ],
  samsung: [
    { kod: 'E1 01', anlam: 'İç ünite sensör hatası', cozum: 'Sensör kontrolü.' },
    { kod: 'E2 21', anlam: 'Dondurma koruma', cozum: 'Filtre temizliği yapın.' },
    { kod: 'E4 64', anlam: 'EEPROM hatası', cozum: 'Ana kart değişimi.' },
    { kod: 'E5 54', anlam: 'Kompresör hatası', cozum: 'Servis gerekli.' },
  ],
  lg: [
    { kod: 'CH01', anlam: 'İç ünite sensör arızası', cozum: 'NTC sensör kontrolü.' },
    { kod: 'CH02', anlam: 'Dış ünite sensör arızası', cozum: 'Dış ünite servisi.' },
    { kod: 'CH05', anlam: 'EEPROM hatası', cozum: 'Ana kart değişimi gerekebilir.' },
    { kod: 'CH10', anlam: 'Kompresör aşırı akım', cozum: 'Kompresör kontrolü.' },
  ],
};

// Hizmetler
const hizmetler = [
  { icon: Wind, title: 'Klima Montaj', desc: 'Profesyonel kurulum ve devreye alma', price: '₺2.500\'den başlayan' },
  { icon: Wrench, title: 'Klima Bakım', desc: 'Yıllık periyodik bakım ve gaz kontrolü', price: '₺800\'den başlayan' },
  { icon: Snowflake, title: 'Klima Gaz Dolumu', desc: 'R410A, R32 ve R22 gaz dolumu', price: '₺1.200\'den başlayan' },
  { icon: ThermometerSun, title: 'Klima Tamir', desc: 'Her marka klima arıza onarımı', price: '₺600\'den başlayan' },
];

// SSS
const sikSorulanSorular = [
  {
    soru: 'Klima ne sıklıkla bakım yaptırılmalı?',
    cevap: 'Klimanızı yılda en az 2 kez (yaz ve kış sezonu öncesi) bakım yaptırmanız önerilir. Düzenli bakım enerji tüketimini %15-20 azaltır ve cihaz ömrünü uzatır.'
  },
  {
    soru: 'Klima gazı ne zaman doldurulmalı?',
    cevap: 'Klimanız yeterli soğutma yapmıyorsa, buzlanma varsa veya dış ünite sıcak hava üflemiyorsa gaz kaçağı olabilir. Profesyonel servis ile gaz seviyesi kontrol edilmeli.'
  },
  {
    soru: 'Klima montaj ücreti ne kadar?',
    cevap: 'Split klima montajı 2.500-4.000 TL arasında değişir. Fiyat; kat yüksekliği, boru mesafesi ve montaj zorluğuna göre belirlenir.'
  },
  {
    soru: 'Inverter klima mı normal klima mı?',
    cevap: 'Inverter klimalar %40-50 daha az enerji tüketir ve sessiz çalışır. Uzun vadede elektrik tasarrufu sağlar. Yaz aylarında yoğun kullanım için inverter önerilir.'
  },
  {
    soru: 'Klimam neden kötü kokuyor?',
    cevap: 'Filtre kirliliği, evaporatör üzerinde bakteri/küf oluşumu kötü kokunun ana sebebidir. Profesyonel kimyasal temizlik (dezenfeksiyon) sorunu çözer.'
  },
  {
    soru: 'Hangi BTU klimayı almalıyım?',
    cevap: '12.000 BTU: 20-25 m², 18.000 BTU: 25-35 m², 24.000 BTU: 35-50 m² için uygundur. Güneş alan odalar için bir üst BTU tercih edilmeli.'
  },
];

const KlimaServisi = () => {
  const navigate = useNavigate();
  const [selectedMarka, setSelectedMarka] = useState('daikin');
  const [openFaq, setOpenFaq] = useState(null);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "HizmetGeliyo Klima Servisi",
    "description": "Türkiye genelinde profesyonel klima montaj, bakım, gaz dolumu ve tamir hizmetleri. Tüm marka ve modellerde uzman servis.",
    "url": "https://hizmetgeliyo.com/klima-servisi",
    "telephone": "+90-850-XXX-XXXX",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "TR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 39.9334,
      "longitude": 32.8597
    },
    "openingHours": "Mo-Su 07:00-23:00",
    "priceRange": "₺₺",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "1250"
    },
    "areaServed": sehirler.map(s => s.name)
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      <Helmet>
        <title>Klima Servisi - Montaj, Bakım, Gaz Dolumu | HizmetGeliyo</title>
        <meta name="description" content="Profesyonel klima servisi: montaj, bakım, gaz dolumu, tamir. Daikin, Samsung, LG, Mitsubishi tüm markalar. 7/24 acil servis, 30 gün garanti. Ücretsiz keşif!" />
        <meta name="keywords" content="klima servisi, klima montaj, klima bakımı, klima gaz dolumu, klima tamiri, split klima montaj, inverter klima servisi, klima temizliği, klima arıza, acil klima servisi" />
        <link rel="canonical" href="https://hizmetgeliyo.com/klima-servisi" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-cyan-600 to-teal-500"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-300 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Snowflake className="w-5 h-5 text-white" />
              <span className="text-white font-medium">Türkiye Geneli Klima Servisi</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Profesyonel <span className="text-cyan-200">Klima Servisi</span>
            </h1>
            
            <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8">
              Montaj, bakım, gaz dolumu ve tamir. Tüm marka ve modellerde uzman servis.
              <strong className="text-white"> 7/24 acil servis</strong>, 30 gün iş garantisi.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button 
                onClick={() => navigate('/talep/klima')}
                className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-4 rounded-full text-lg font-semibold shadow-xl"
              >
                <Wind className="w-5 h-5 mr-2" />
                Ücretsiz Teklif Al
              </Button>
              <Button 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full text-lg"
              >
                <Phone className="w-5 h-5 mr-2" />
                Hemen Ara
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-6 text-white/90">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-cyan-300" />
                <span>500+ Onaylı Usta</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-cyan-300" />
                <span>30 Gün Garanti</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-cyan-300" />
                <span>4.8/5 Müşteri Puanı</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-cyan-300" />
                <span>Aynı Gün Servis</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hizmetler */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Klima Hizmetlerimiz</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Tüm klima ihtiyaçlarınız için profesyonel çözümler</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {hizmetler.map((hizmet, idx) => (
              <div 
                key={idx}
                className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 border border-blue-100 group cursor-pointer"
                onClick={() => navigate('/talep/klima')}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <hizmet.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{hizmet.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{hizmet.desc}</p>
                <p className="text-blue-600 font-semibold">{hizmet.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Markalar */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Tüm Markalara Hizmet</h2>
            <p className="text-gray-600">Hangi marka klimanız olursa olsun, uzman ekibimiz yanınızda</p>
          </div>

          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {klimaMarkalari.map((marka, idx) => (
              <div 
                key={idx}
                className="bg-white rounded-xl p-4 flex items-center justify-center h-20 shadow-sm hover:shadow-md transition-shadow border"
                style={{ borderColor: marka.color + '30' }}
              >
                <span className="font-bold text-gray-700">{marka.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Arıza Kodları */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Klima Arıza Kodları</h2>
            <p className="text-gray-600">Klimanızda gördüğünüz hata kodunun ne anlama geldiğini öğrenin</p>
          </div>

          {/* Marka Seçimi */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {Object.keys(klimaArizaKodlari).map((marka) => (
              <button
                key={marka}
                onClick={() => setSelectedMarka(marka)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedMarka === marka
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {marka.charAt(0).toUpperCase() + marka.slice(1)}
              </button>
            ))}
          </div>

          {/* Arıza Tablosu */}
          <div className="bg-gray-50 rounded-2xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-blue-600 text-white">
                <tr>
                  <th className="px-4 py-3 text-left">Kod</th>
                  <th className="px-4 py-3 text-left">Anlam</th>
                  <th className="px-4 py-3 text-left hidden md:table-cell">Çözüm</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {klimaArizaKodlari[selectedMarka]?.map((ariza, idx) => (
                  <tr key={idx} className="hover:bg-blue-50">
                    <td className="px-4 py-3 font-mono font-bold text-blue-600">{ariza.kod}</td>
                    <td className="px-4 py-3 text-gray-700">{ariza.anlam}</td>
                    <td className="px-4 py-3 text-gray-600 text-sm hidden md:table-cell">{ariza.cozum}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="text-center mt-8">
            <Button 
              onClick={() => navigate('/talep/klima')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Arıza Servis Talebi Oluştur
            </Button>
          </div>
        </div>
      </section>

      {/* Hizmet Verilen Şehirler */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Hizmet Verilen Şehirler</h2>
            <p className="text-gray-600">Türkiye'nin dört bir yanında yanınızdayız</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {sehirler.map((sehir, idx) => (
              <div 
                key={idx}
                className="bg-white rounded-xl p-4 hover:shadow-lg transition-all cursor-pointer border border-gray-100"
                onClick={() => navigate('/talep/klima')}
              >
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="font-semibold text-gray-900">{sehir.name}</p>
                    <p className="text-xs text-gray-500">{sehir.population} nüfus</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SSS */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Sıkça Sorulan Sorular</h2>
            <p className="text-gray-600">Klima servisi hakkında merak edilenler</p>
          </div>

          <div className="space-y-4">
            {sikSorulanSorular.map((faq, idx) => (
              <div key={idx} className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full px-6 py-4 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 text-left">{faq.soru}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === idx && (
                  <div className="px-6 py-4 bg-gray-50 border-t">
                    <p className="text-gray-600">{faq.cevap}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Klima Sorununuzu Hemen Çözelim
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            Montaj, bakım, gaz dolumu veya tamir - ne ihtiyacınız varsa buradayız.
          </p>
          <Button 
            onClick={() => navigate('/talep/klima')}
            className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-4 rounded-full text-lg font-semibold"
          >
            Ücretsiz Teklif Al <ArrowRight className="w-5 h-5 ml-2 inline" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default KlimaServisi;
